import React from "react";
import { Button } from "./ui/button";
import { Download, FileText } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { downloadCSV, stampedName } from "../lib/csv";
import { exportPDF } from "../lib/pdf";
import { useToast } from "../hooks/use-toast";

/**
 * Shared export controls.
 * Renders:
 *   - Admin: [Download CSV] [Export PDF]
 *   - Member: [Export PDF] only
 *
 * Props:
 *   csv:       { filename, headers, rows }                 // required for admin view
 *   pdf:       { title, subtitle, filename, orientation,   // always required
 *                columns, rows, summary, foot }
 *   disabled:  boolean — disable both buttons when empty
 *   testIdPrefix: string — overrides default "export"
 *   adminOnly: boolean — when true, hide ALL buttons for members (use for Admin-only pages)
 *   className: string
 */
export default function ExportButtons({
  csv,
  pdf,
  disabled = false,
  testIdPrefix = "export",
  adminOnly = false,
  className = "",
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user?.role === "admin";

  if (adminOnly && !isAdmin) return null;

  const handleCSV = () => {
    try {
      if (!csv || !csv.headers) {
        toast({ title: "Nothing to export" });
        return;
      }
      const name = csv.filename ? csv.filename : stampedName("export");
      downloadCSV(name, csv.headers, csv.rows || []);
    } catch (e) {
      toast({ title: "CSV export failed", description: e.message || "Try again" });
    }
  };

  const handlePDF = () => {
    try {
      if (!pdf) {
        toast({ title: "Nothing to export" });
        return;
      }
      exportPDF(pdf);
    } catch (e) {
      toast({ title: "PDF export failed", description: e.message || "Try again" });
    }
  };

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`}>
      {isAdmin && csv && (
        <Button
          onClick={handleCSV}
          disabled={disabled}
          data-testid={`${testIdPrefix}-csv-btn`}
          className="bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 disabled:opacity-50"
        >
          <Download size={14} className="mr-2" /> Export CSV
        </Button>
      )}
      {pdf && (
        <Button
          onClick={handlePDF}
          disabled={disabled}
          data-testid={`${testIdPrefix}-pdf-btn`}
          className="bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 disabled:opacity-50"
        >
          <FileText size={14} className="mr-2" /> Export PDF
        </Button>
      )}
    </div>
  );
}
